import {
  PRIVACY_CONSENT_PURPOSE_LABELS,
  PRIVACY_PURPOSE_CODE_PATTERN,
  PRIVACY_TEXT_KIND_LABELS,
  PRIVACY_TEXT_KINDS,
  PRIVACY_TEXT_STATUS_LABELS,
  PRIVACY_TEXT_STATUSES,
  type PrivacyText,
  privacyTextKindSchema,
  privacyTextStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Eye, FilePlus2, Pencil, Plus, ScrollText, Send } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Section } from "../../../features/expenses/shared.js";
import { Th } from "../../../features/fuel/shared.js";
import { TableFrame } from "../../../features/market/shared.js";
import {
  currentPublished,
  filterTextGroups,
  groupTexts,
  isSuggestedPurpose,
  purposeOptions,
  type TextGroup,
} from "../../../features/privacy/privacy-values.js";
import {
  usePrivacyPermissions,
  usePrivacyTexts,
  useRefreshPrivacy,
} from "../../../features/privacy/queries.js";
import {
  PrivacyPage,
  purposeLabel,
  Stamp,
  textGroupTitle,
  TextStatusBadge,
} from "../../../features/privacy/shared.js";
import { TextDialog, type TextDialogMode } from "../../../features/privacy/text-dialog.js";
import { TextViewer } from "../../../features/privacy/text-viewer.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

const searchSchema = z.object({
  kind: privacyTextKindSchema.optional().catch(undefined),
  purpose: z.string().regex(PRIVACY_PURPOSE_CODE_PATTERN).optional().catch(undefined),
  status: privacyTextStatusSchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kvkk/metinler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PrivacyTextsRoute,
});

/**
 * KVKK metinleri (Prompt 17 web, ADR 0017 §7): aydınlatma, açık rıza (amaç kodlu) ve ticari ileti onayı metinleri
 * TÜR + AMAÇ grubunda sürüm geçmişiyle. Taslak yazılır / düzenlenir; yayımlanan metin DEĞİŞMEZ ve aynı gruptaki
 * yürürlükteki sürüm aynı anda yürürlükten kalkar. Yazma `privacy.manage`.
 */
function PrivacyTextsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePrivacyPermissions();
  const texts = usePrivacyTexts({ enabled: permissions.canRead });
  const [dialog, setDialog] = useState<TextDialogMode | null>(null);
  return (
    <PrivacyPage
      title={t("menu.privacyTexts")}
      description={t("privacy.texts.description")}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setDialog({ mode: "create" })}>
            <Plus aria-hidden="true" />
            {t("privacy.texts.newDraft")}
          </Button>
        ) : null
      }
    >
      {texts.error ? (
        <Alert tone="danger">{errorMessage(texts.error)}</Alert>
      ) : texts.isPending ? (
        <SkeletonRows rows={6} />
      ) : (
        <TextsBody texts={texts.data} onDialog={setDialog} />
      )}
      {permissions.canManage ? (
        <TextDialog state={dialog} onClose={() => setDialog(null)} texts={texts.data ?? []} />
      ) : null}
    </PrivacyPage>
  );
}

function TextsBody({
  texts,
  onDialog,
}: {
  texts: PrivacyText[];
  onDialog: (state: TextDialogMode) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = usePrivacyPermissions();
  const groups = useMemo(() => groupTexts(texts), [texts]);
  const visible = filterTextGroups(groups, {
    kind: search.kind,
    purposeCode: search.purpose,
    status: search.status,
  });
  const [viewing, setViewing] = useState<PrivacyText | null>(null);
  const [publishing, setPublishing] = useState<PrivacyText | null>(null);

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (texts.length === 0)
    return (
      <EmptyState
        headingLevel={2}
        icon={<ScrollText />}
        title={t("privacy.texts.emptyTitle")}
        description={t("privacy.texts.emptyDescription")}
        action={
          permissions.canManage ? (
            <Button onClick={() => onDialog({ mode: "create" })}>
              <Plus aria-hidden="true" />
              {t("privacy.texts.newDraft")}
            </Button>
          ) : undefined
        }
      />
    );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("privacy.texts.fields.kind")} className="w-60">
          {(control) => (
            <Select
              {...control}
              value={search.kind ?? ""}
              onChange={(event) => setSearch({ kind: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              {PRIVACY_TEXT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {PRIVACY_TEXT_KIND_LABELS[kind]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("privacy.texts.fields.purpose")} className="w-72">
          {(control) => (
            <Select
              {...control}
              value={search.purpose ?? ""}
              onChange={(event) => setSearch({ purpose: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              {purposeOptions(texts).map((code) => (
                <option key={code} value={code}>
                  {isSuggestedPurpose(code) ? `${PRIVACY_CONSENT_PURPOSE_LABELS[code]} (${code})` : code}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("common.status")} className="w-48">
          {(control) => (
            <Select
              {...control}
              value={search.status ?? ""}
              onChange={(event) => setSearch({ status: event.target.value || undefined })}
            >
              <option value="">{t("common.all")}</option>
              {PRIVACY_TEXT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {PRIVACY_TEXT_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>

      {visible.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<ScrollText />}
          title={t("privacy.texts.noMatchTitle")}
          description={t("privacy.texts.noMatchDescription")}
        />
      ) : (
        visible.map((group) => (
          <TextGroupSection
            key={group.key}
            group={group}
            canManage={permissions.canManage}
            onView={setViewing}
            onEdit={(text) => onDialog({ mode: "edit", text })}
            onPublish={setPublishing}
            onNewVersion={() => {
              const latest = group.current ?? group.versions[0]!;
              onDialog({
                mode: "create",
                preset: {
                  kind: group.kind,
                  purposeCode: group.purposeCode,
                  title: latest.title,
                  body: latest.body,
                  language: latest.language,
                },
              });
            }}
          />
        ))
      )}

      <TextViewer text={viewing} onClose={() => setViewing(null)} />
      {permissions.canManage ? (
        <PublishDialog text={publishing} texts={texts} onClose={() => setPublishing(null)} />
      ) : null}
    </>
  );
}

/** Bir tür + amaç grubunun sürüm geçmişi (yeniden eskiye). */
function TextGroupSection({
  group,
  canManage,
  onView,
  onEdit,
  onPublish,
  onNewVersion,
}: {
  group: TextGroup;
  canManage: boolean;
  onView: (text: PrivacyText) => void;
  onEdit: (text: PrivacyText) => void;
  onPublish: (text: PrivacyText) => void;
  onNewVersion: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const title = textGroupTitle(group.kind, group.purposeCode);
  const purpose = purposeLabel(group.purposeCode);
  return (
    <Section
      title={title}
      actions={
        <>
          {group.current ? (
            <StatusBadge tone="success" className="whitespace-nowrap">
              {t("privacy.texts.currentRevision", { revision: group.current.revision })}
            </StatusBadge>
          ) : (
            <StatusBadge tone="warning" className="whitespace-nowrap">
              {t("privacy.texts.noCurrent")}
            </StatusBadge>
          )}
          {canManage && group.draft === null ? (
            <Button variant="outline" size="sm" onClick={onNewVersion}>
              <FilePlus2 aria-hidden="true" />
              {t("privacy.texts.newVersion")}
              <span className="sr-only"> ({title})</span>
            </Button>
          ) : null}
        </>
      }
    >
      {purpose ? <p className="-mt-2 text-sm text-muted-foreground">{purpose}</p> : null}
      <TableFrame label={t("privacy.texts.historyCaption", { group: title })}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("privacy.texts.fields.revision")}</Th>
            <Th>{t("privacy.texts.fields.title")}</Th>
            <Th>{t("privacy.texts.fields.language")}</Th>
            <Th>{t("common.status")}</Th>
            <Th>{t("privacy.texts.fields.publishedAt")}</Th>
            <Th>{t("privacy.texts.fields.retiredAt")}</Th>
            <Th align="right">{t("common.actions")}</Th>
          </tr>
        </thead>
        <tbody>
          {group.versions.map((text) => (
            <tr key={text.id} className="border-t" data-revision={text.revision}>
              <td className="px-3 py-2 font-mono font-semibold">v{text.revision}</td>
              <td className="min-w-48 max-w-80 px-3 py-2">
                <span className="line-clamp-2">{text.title}</span>
              </td>
              <td className="px-3 py-2 uppercase">{text.language}</td>
              <td className="px-3 py-2">
                <TextStatusBadge status={text.status} />
              </td>
              <td className="px-3 py-2">
                <Stamp value={text.publishedAt} />
              </td>
              <td className="px-3 py-2">
                <Stamp value={text.retiredAt} />
              </td>
              <td className="px-3 py-2">
                {/* `relative`: düğmelerdeki `sr-only` metin kaydırma çerçevesinin DIŞINA taşıp sayfayı genişletmesin. */}
                <div className="relative flex flex-wrap justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onView(text)}>
                    <Eye aria-hidden="true" />
                    {t("privacy.texts.view")}
                    <span className="sr-only"> (v{text.revision})</span>
                  </Button>
                  {canManage && text.status === "DRAFT" ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => onEdit(text)}>
                        <Pencil aria-hidden="true" />
                        {t("common.edit")}
                        <span className="sr-only"> (v{text.revision})</span>
                      </Button>
                      <Button size="sm" onClick={() => onPublish(text)}>
                        <Send aria-hidden="true" />
                        {t("privacy.texts.publish")}
                        <span className="sr-only"> (v{text.revision})</span>
                      </Button>
                    </>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableFrame>
    </Section>
  );
}

/**
 * Yayımlama onayı: yayımlanan metin DEĞİŞTİRİLEMEZ ve aynı tür + amaçtaki yürürlükteki sürüm (vN) AYNI anda
 * yürürlükten kalkar — kullanıcı hangi sürümün kalkacağını görerek onaylar.
 */
function PublishDialog({
  text,
  texts,
  onClose,
}: {
  text: PrivacyText | null;
  texts: readonly PrivacyText[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPrivacy();
  const current = text ? currentPublished(texts, text) : null;
  const publish = useMutation({
    mutationFn: async (target: PrivacyText) =>
      (
        await api.POST("/privacy/texts/{id}/publish", {
          params: { path: { id: target.id } },
          body: { version: target.version },
        })
      ).data!,
    onSuccess: async (published) => {
      await refresh();
      toast({ tone: "success", title: t("privacy.texts.published", { revision: published.revision }) });
    },
  });
  return (
    <ConfirmDialog
      open={text !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={text ? t("privacy.texts.publishTitle", { revision: text.revision }) : ""}
      description={
        current
          ? t("privacy.texts.publishConfirmCurrent", { revision: current.revision })
          : t("privacy.texts.publishConfirmFirst")
      }
      confirmLabel={t("privacy.texts.publish")}
      onConfirm={async () => {
        if (!text) return;
        try {
          await publish.mutateAsync(text);
        } catch (caught) {
          toast({ tone: "danger", title: errorMessage(caught) });
        }
      }}
    >
      {text ? (
        <p className="text-sm">
          <span className="font-semibold">{textGroupTitle(text.kind, text.purposeCode)}</span> — v
          {text.revision}: {text.title}
        </p>
      ) : null}
    </ConfirmDialog>
  );
}
